import type { OrgAccommodationRequest } from '~/gql/accommodations/accommodations'
import { useMutation, useQuery } from '@apollo/client/react'
import { Accessibility } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import Spinner from '~/components/Spinner'
import { GQL_ACCOMMODATION_REQUESTS, GQL_RESPOND_ACCOMMODATION } from '~/gql/accommodations/accommodations'
import { useBranding } from '~/helpers/branding/branding-context'
import { DATE_WITH_YEAR, formatSessionDate } from '~/helpers/dates/session-dates'
import { tourAnchor } from '~/helpers/tour/anchors'
import AccommodationDialog from './AccommodationDialog'
import AccommodationsTable from './AccommodationsTable'
import ReferentCard from './ReferentCard'

/**
 * Accueil des personnes en situation de handicap (#327), aux admins : le
 * référent de l'organisme, et les demandes d'aménagement des apprenants
 * (Qualiopi, indicateur 26). Donnée de santé : jamais montrée aux formateurs.
 */
function AccommodationsPage() {
  const { t, i18n } = useTranslation()
  const { timezone } = useBranding()
  const { data, loading, refetch } = useQuery(GQL_ACCOMMODATION_REQUESTS, { fetchPolicy: 'cache-and-network' })
  const [respond, { loading: saving }] = useMutation(GQL_RESPOND_ACCOMMODATION)
  const [opened, setOpened] = useState<OrgAccommodationRequest | null>(null)
  const requests = data?.accommodationRequests ?? []
  const formatDate = (value: unknown) => formatSessionDate(value, i18n.language, timezone, DATE_WITH_YEAR)

  const save = async (id: string, input: { response: string, handled: boolean }) => {
    try {
      await respond({ variables: { input: { id, response: input.response || null, handled: input.handled } } })
      toast.success(t('accommodations.saved'))
      setOpened(null)
      void refetch()
    } catch {
      toast.error(t('accommodations.saveFailed'))
    }
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <p className="text-sm text-muted-foreground" {...tourAnchor('accommodations.intro')}>{t('accommodations.intro')}</p>
      <ReferentCard />
      <section aria-labelledby="accommodations-requests" className="flex flex-col gap-3" {...tourAnchor('accommodations.list')}>
        <h2 id="accommodations-requests" className="text-lg font-semibold">{t('accommodations.requests')}</h2>
        {loading && !data
          ? <div className="flex justify-center p-10"><Spinner className="size-6" /></div>
          : requests.length === 0
            ? (
                <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center text-muted-foreground">
                  <Accessibility className="size-8" />
                  <p className="text-sm">{t('accommodations.empty')}</p>
                </div>
              )
            : <div className="overflow-x-auto rounded-lg border"><AccommodationsTable requests={requests} formatDate={formatDate} onOpen={setOpened} /></div>}
      </section>
      {opened && (
        <AccommodationDialog request={opened} formatDate={formatDate} busy={saving} onCancel={() => setOpened(null)} onSave={input => void save(opened.id, input)} />
      )}
    </div>
  )
}

export default AccommodationsPage
